// client/src/types/support/inbox.ts
import { Timestamp } from 'firebase/firestore';
import type { AssigneeStatus } from '../../lib/support/assignee-identity';
import type { CallLog, CallLogStatus, CallLogFlag } from '../callLog';
import type { SupportCase, CaseStatus, CasePriority, CaseType } from '../supportCase';

/**
 * Unified inbox item — wraps a call log or support case
 * with a channel discriminant for polymorphic rendering.
 */
export type InboxChannel = 'call' | 'web_lead' | 'case';

export interface InboxItemBase {
  /** Unique ID (callLogId or caseId) */
  id: string;
  channel: InboxChannel;
  /** Primary display name (caller name or requester name) */
  displayName: string;
  phone?: string;
  email?: string;
  /** Normalized branch display name (e.g. "Tracy", "Seaside") — always use name, not ID */
  branch?: string;
  /**
   * The branch text as stored, when `branch` was re-resolved to a configured branch name (or to
   * no branch). Kept for what must use the raw value: the SMS send-from default and search.
   */
  storedBranch?: string;
  /** Assignee display name */
  assignee?: string;
  /** Assignee userId (for "Mine" filter) */
  assigneeUserId?: string;
  /**
   * Whether the assignee resolves to a real user. Set by useSupportInbox via
   * getAssigneeIdentity — `unlinked` means a name was stored that matches no
   * OfficeHub user, which counts as unassigned but still renders the name.
   */
  assigneeStatus?: AssigneeStatus;
  /** When this item was created */
  createdAt: Timestamp;
  /** When this item was last updated */
  updatedAt: Timestamp;
  /** Whether this item is open or closed */
  isOpen: boolean;
  /** Urgency tier for sorting (1=critical, 2=high, 3=normal) */
  urgencyTier: 1 | 2 | 3;
  /** Number of items sharing this phone number (for repeat caller badge). Set by useSupportInbox. */
  repeatCount?: number;
  /** UserIds who have unread @mentions on this item */
  unreadMentionUserIds?: string[];
}

export interface CallLogInboxItem extends InboxItemBase {
  channel: 'call';
  status: CallLogStatus;
  flags: CallLogFlag[];
  notes: string;
  /** The full call log document */
  callLog: CallLog;
}

export interface WebLeadInboxItem extends InboxItemBase {
  channel: 'web_lead';
  status: CaseStatus;
  priority: CasePriority;
  subject: string;
  /** The full support case document */
  supportCase: SupportCase;
}

export interface CaseInboxItem extends InboxItemBase {
  channel: 'case';
  status: CaseStatus;
  priority: CasePriority;
  subject: string;
  /** The full support case document */
  supportCase: SupportCase;
}

export type InboxItem = CallLogInboxItem | WebLeadInboxItem | CaseInboxItem;

/**
 * Filter state for the inbox
 */
export interface InboxFilters {
  channel: 'all' | 'calls' | 'web_leads' | 'cases' | 'mentions';
  branch: string; // 'all', UNASSIGNED_BRANCH_FILTER, or branch name
  status: 'all' | 'open' | 'closed';
  assignee: 'all' | 'mine' | 'unassigned' | string; // 'all', 'mine', 'unassigned', or userId
  search: string; // free-text search across name, phone, notes
  type?: 'all' | CaseType; // Case type filter (rfp, question, etc.)
  /** When true, restrict call logs to those flagged for AI-extraction review (Startel inbound). */
  needsReviewOnly?: boolean;
}

/**
 * Item count per channel tab, for the filter bar badges.
 *
 * One key per `InboxFilters['channel']` value. Produced by
 * `computeChannelCounts` and consumed by `InboxFilterBar`; it lives here rather
 * than in either of them so the two can never drift apart.
 */
export type InboxChannelCounts = Record<InboxFilters['channel'], number>;

/**
 * Branch filter value for items whose branch is missing or is not a configured branch. Not a
 * branch name a person would type, and inside the saved-default charset ([a-zA-Z0-9 _-]).
 */
export const UNASSIGNED_BRANCH_FILTER = '__unassigned__';

export const DEFAULT_INBOX_FILTERS: InboxFilters = {
  channel: 'all',
  branch: 'all',
  status: 'open',
  assignee: 'all',
  search: '',
  needsReviewOnly: false,
};

/**
 * Activity log entry — stored as subcollection under parent docs
 */
export interface ActivityEntry {
  activityId: string;
  timestamp: Timestamp;
  authorEmail: string;
  authorName: string;
  text: string;
  /** Auto-generated entries (status change, escalation) vs manual notes */
  isSystem?: boolean;
  /** UserIds of users @mentioned in this note */
  mentionedUserIds?: string[];
  /** Timestamp when this note was last edited (undefined if never edited) */
  editedAt?: Timestamp;
}

/**
 * User-level support preferences
 */
export interface SupportPreferences {
  defaultLandingPage: 'dashboard' | 'inbox';
  inboxDefaults: InboxFilters;
}

export const DEFAULT_SUPPORT_PREFERENCES: SupportPreferences = {
  defaultLandingPage: 'dashboard',
  inboxDefaults: DEFAULT_INBOX_FILTERS,
};

/**
 * Day group for timeline display
 */
export interface InboxDayGroup {
  date: string; // ISO date string (YYYY-MM-DD)
  label: string; // "Today", "Yesterday", "Mar 21, 2026"
  items: InboxItem[];
}

/**
 * Query key factory for inbox
 */
export const supportInboxKeys = {
  all: ['supportInbox'] as const,
  lists: () => [...supportInboxKeys.all, 'list'] as const,
  list: (filters: InboxFilters) => [...supportInboxKeys.lists(), filters] as const,
  closedPages: () => [...supportInboxKeys.all, 'closed'] as const,
  closedPage: (channel: string, page: number) => [...supportInboxKeys.closedPages(), channel, page] as const,
};

export const supportPreferencesKeys = {
  all: ['supportPreferences'] as const,
  user: (userId: string) => [...supportPreferencesKeys.all, userId] as const,
};

export const activityLogKeys = {
  all: ['activityLog'] as const,
  list: (parentCollection: string, parentId: string) => [...activityLogKeys.all, parentCollection, parentId] as const,
};
